'use client';

import { c as _c } from "react/compiler-runtime";
import { Select as RedesignSelect } from '@systemeio/components';
import type { ReactElement, ReactNode } from 'react';
import type { FormSelectIdType, FormSelectProps } from 'shared/components/form-select/form-select';
import { OptionsSkeleton } from 'shared/components/form-select/form-select';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
interface SelectProps<T extends FormSelectIdType> extends Omit<FormSelectProps<T>, 'ariaLabel' | 'inputRef' | 'label' | 'statusIcon' | 'withoutCloseIcon'> {
  /**
   * Consumer-owned trigger element. Rendered through `Select.Trigger unstyled`, so it
   * keeps its own chrome (segmented controls glued to a sibling input, inline link
   * buttons) and still remaps to a Bottom Drawer below 744px. Its children win over
   * ours, so the element supplies its own caption.
   */
  trigger?: ReactElement;
}
function Select(t0) {
  const $ = _c(52);
  const {
    data,
    dataTest,
    description,
    disabled,
    error,
    isPreFetching,
    onChange,
    onClose,
    onOpen,
    placeholder,
    required,
    size,
    staticData,
    trigger,
    value
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const options = data ?? staticData;
  const isDisabled = disabled || isPreFetching || value !== undefined && options === undefined;
  let t1;
  if ($[0] !== placeholder || $[1] !== t) {
    t1 = placeholder ?? t("global.select");
    $[0] = placeholder;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const accessibleLabel = t1;
  let t2;
  if ($[3] !== options) {
    t2 = options ?? [];
    $[3] = options;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = t2.map(_temp);
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const items = t3;
  let content;
  if (options === undefined) {
    let t4;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <OptionsSkeleton />;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    content = t4;
  } else {
    if (options.length === 0) {
      let t4;
      if ($[8] !== t) {
        t4 = t("dashboard.droplist.default_description");
        $[8] = t;
        $[9] = t4;
      } else {
        t4 = $[9];
      }
      let t5;
      if ($[10] !== t) {
        t5 = t("dashboard.droplist.default_title");
        $[10] = t;
        $[11] = t5;
      } else {
        t5 = $[11];
      }
      let t6;
      if ($[12] !== t4 || $[13] !== t5) {
        t6 = <RedesignSelect.Empty description={t4}>{t5}</RedesignSelect.Empty>;
        $[12] = t4;
        $[13] = t5;
        $[14] = t6;
      } else {
        t6 = $[14];
      }
      content = t6;
    } else {
      let t4;
      if ($[15] !== options) {
        t4 = options.map(_temp2);
        $[15] = options;
        $[16] = t4;
      } else {
        t4 = $[16];
      }
      let t5;
      if ($[17] !== t4) {
        t5 = <RedesignSelect.List>{t4}</RedesignSelect.List>;
        $[17] = t4;
        $[18] = t5;
      } else {
        t5 = $[18];
      }
      content = t5;
    }
  }
  const t4 = isPreFetching || undefined;
  let t5;
  if ($[19] !== onClose || $[20] !== onOpen) {
    t5 = open => {
      if (open) {
        onOpen?.();
      } else {
        onClose?.();
      }
    };
    $[19] = onClose;
    $[20] = onOpen;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== onChange) {
    t6 = option_1 => onChange(option_1 ?? undefined);
    $[22] = onChange;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const t7 = value ?? null;
  let t8;
  if ($[24] !== accessibleLabel || $[25] !== dataTest || $[26] !== isPreFetching || $[27] !== trigger) {
    t8 = trigger ? <RedesignSelect.Trigger {...dataTest} aria-label={accessibleLabel} className={isPreFetching ? "animate-pulse" : undefined} render={trigger} unstyled={true} /> : <RedesignSelect.Input {...dataTest} aria-label={accessibleLabel} className={isPreFetching ? "animate-pulse" : undefined} />;
    $[24] = accessibleLabel;
    $[25] = dataTest;
    $[26] = isPreFetching;
    $[27] = trigger;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== content) {
    t9 = <RedesignSelect.Content>{content}</RedesignSelect.Content>;
    $[29] = content;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== accessibleLabel || $[32] !== isDisabled || $[33] !== items || $[34] !== placeholder || $[35] !== required || $[36] !== size || $[37] !== t5 || $[38] !== t6 || $[39] !== t7 || $[40] !== t8 || $[41] !== t9) {
    t10 = <RedesignSelect aria-label={accessibleLabel} disabled={isDisabled} drawerTitle={accessibleLabel} isItemEqualToValue={Object.is} itemToStringValue={String} items={items} onOpenChange={t5} onValueChange={t6} placeholder={placeholder} required={required} size={size} value={t7}>{t8}{t9}</RedesignSelect>;
    $[31] = accessibleLabel;
    $[32] = isDisabled;
    $[33] = items;
    $[34] = placeholder;
    $[35] = required;
    $[36] = size;
    $[37] = t5;
    $[38] = t6;
    $[39] = t7;
    $[40] = t8;
    $[41] = t9;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  let t11;
  if ($[43] !== description) {
    t11 = description ? <RedesignSelect.Description>{description}</RedesignSelect.Description> : null;
    $[43] = description;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  let t12;
  if ($[45] !== error) {
    t12 = error ? <RedesignSelect.Error>{error}</RedesignSelect.Error> : null;
    $[45] = error;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  let t13;
  if ($[47] !== t10 || $[48] !== t11 || $[49] !== t12 || $[50] !== t4) {
    t13 = <div aria-busy={t4} className="inline-grid gap-2">{t10}{t11}{t12}</div>;
    $[47] = t10;
    $[48] = t11;
    $[49] = t12;
    $[50] = t4;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  return t13;
}
function _temp2(option_0) {
  return <RedesignSelect.Item data-test-id={option_0.dataTestAttribute} disabled={option_0.disabled} key={String(option_0.id)} value={option_0.id}><RedesignSelect.ItemText>{option_0.customCaption ?? option_0.caption}</RedesignSelect.ItemText>{option_0.additionalRender}<RedesignSelect.ItemIndicator /></RedesignSelect.Item>;
}
function _temp(option) {
  return {
    label: option.caption,
    value: option.id
  };
}
export { Select };
export type { SelectProps };